import { useState } from 'react'
import { useStore }  from '@/store/StoreContext'
import { useT }      from '@/i18n/I18nContext'
import { useAuth }   from '@/auth/AuthContext'

// ─── Brand tokens ──────────────────────────────────────────────────────────
const T = {
  agniCore:  '#EA580C',
  agniGlow:  '#F97316',
  agniTip:   '#FDE68A',
  vidyaGold: '#C8A84B',
  nightInk:  '#0F1A2E',
  surface:   '#162035',
  surfaceHi: '#1C2A44',
  border:    '#243350',
  textHi:    '#F1F5FF',
  textMid:   '#A8B8D0',
  textLow:   '#5C7090',
  blue:      '#4B8EF5',
  violet:    '#B07EF0',
  green:     '#3DB87A',
  teal:      '#2BB8D4',
  amber:     '#E8A94A',
}

const EXPANDED_W  = 256
const COLLAPSED_W = 64

// ─── Nav data ─────────────────────────────────────────────────────────────
const LEARNING_MODS = [
  { id: 'learn',           icon: '🎮', label: 'Flashcard Activity',  highlight: true },
  { id: 'worksheet-learn', icon: '📝', label: 'Worksheet Activity',  highlight: true },
  { id: 'activity-learn',  icon: '🎨', label: 'Composite Activity',  highlight: true },
  { id: 'storybook-learn', icon: '📖', label: 'StoryBook Learning',  highlight: true },
]

// CHANGE 1: added srt-screener with explicit label + gold accent
const CONTENT_MODS = [
  { id: 'collection',   icon: '🗂️', color: T.amber    },
  { id: 'flashcard',    icon: '🃏',  color: T.agniGlow },
  { id: 'worksheet',    icon: '📋', color: T.blue      },
  { id: 'activity',     icon: '🖼️', color: T.violet    },
  { id: 'storybook',    icon: '📖', color: T.teal      },
  { id: 'workplan',     icon: '📅', color: T.green     },
  { id: 'progress',     icon: '📊', color: T.violet    },
  { id: 'readiness',    icon: '🔍', color: T.blue      },
  { id: 'srt-screener', icon: '⚡', color: '#F5D000', label: 'Quick Screener' },
]

const ROLE_META = {
  superadmin: { color: T.violet,   icon: '👑', label: 'Super Admin' },
  admin:      { color: T.agniGlow, icon: '⚙️', label: 'Admin'       },
  teacher:    { color: T.green,    icon: '📚', label: 'Teacher'     },
  parent:     { color: T.blue,     icon: '👨‍👩‍👧', label: 'Parent'      },
  // Added alongside the research dashboard work — view-only role for NCAHT
  // colleagues outside NISH (and superadmin, who already has full access).
  researcher: { color: T.teal,     icon: '🔬', label: 'Researcher'  },
}

const LANGS = [
  { code: 'en', label: 'EN' },
  { code: 'ml', label: 'ML' },
  { code: 'hi', label: 'HI' },
  { code: 'ta', label: 'TA' },
  { code: 'te', label: 'TE' },
]

// ─── Vidyagni mark ────────────────────────────────────────────────────────
function Mark({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 68 68" fill="none" style={{ flexShrink: 0 }}>
      <rect width="68" height="68" rx={Math.round(size * 0.25)} fill="#17243A" />
      <path d="M34 8C34 8 22 22 22 34C22 45 34 52 34 52C34 52 46 45 46 34C46 22 34 8 34 8Z" fill="#C2410C" />
      <path d="M34 13C34 13 25 25 25 34C25 43 34 49 34 49C34 49 43 43 43 34C43 25 34 13 34 13Z" fill={T.agniCore} />
      <path d="M34 18C34 18 28 28 28 34C28 40 34 46 34 46C34 46 40 40 40 34C40 28 34 18 34 18Z" fill={T.agniGlow} />
      <path d="M34 26C34 26 30 31 30 35C30 39 34 43 34 43C34 43 38 39 38 35C38 31 34 26 34 26Z" fill={T.agniTip} />
      <path d="M26 55Q30 49 34 55Q38 49 42 55" stroke={T.vidyaGold} strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="20" y1="59" x2="48" y2="59" stroke={T.vidyaGold} strokeWidth="2.8" strokeLinecap="round" />
    </svg>
  )
}

// ─── Hamburger icon ───────────────────────────────────────────────────────
function HamburgerIcon({ open }) {
  const bar = (y, w = 16) => (
    <rect
      x={open ? 8 : (16 - w) / 2} y={y} width={open ? 16 : w} height="2"
      rx="1" fill={T.textHi}
      style={{ transition: 'all .25s ease' }}
    />
  )
  return (
    <svg width={32} height={32} viewBox="0 0 32 32">
      {open ? (
        <>
          <rect x="8" y="15" width="16" height="2" rx="1" fill={T.textHi}
            style={{ transform: 'rotate(45deg)', transformOrigin: '16px 16px', transition: 'all .25s' }} />
          <rect x="8" y="15" width="16" height="2" rx="1" fill={T.textHi}
            style={{ transform: 'rotate(-45deg)', transformOrigin: '16px 16px', transition: 'all .25s' }} />
        </>
      ) : (
        <>
          {bar(9, 16)}
          {bar(15, 12)}
          {bar(21, 16)}
        </>
      )}
    </svg>
  )
}

// ─── Divider ──────────────────────────────────────────────────────────────
function Divider() {
  return <div style={{ height: 1, background: T.border, margin: '6px 8px 8px' }} />
}

// ─── Section label ────────────────────────────────────────────────────────
function SectionLabel({ children, open }) {
  if (!open) return null
  return (
    <div style={{
      fontSize: 10, fontWeight: 800, color: T.textLow,
      letterSpacing: 1.6, padding: '12px 12px 5px',
      textTransform: 'uppercase', fontFamily: "'Inter',sans-serif",
    }}>
      {children}
    </div>
  )
}

// ─── Tooltip ──────────────────────────────────────────────────────────────
function Tooltip({ label, children }) {
  const [vis, setVis] = useState(false)
  return (
    <div
      style={{ position: 'relative' }}
      onMouseEnter={() => setVis(true)}
      onMouseLeave={() => setVis(false)}
    >
      {children}
      {vis && (
        <div style={{
          position: 'absolute', left: '100%', top: '50%', transform: 'translateY(-50%)',
          marginLeft: 10, zIndex: 9999,
          background: T.surfaceHi, color: T.textHi,
          border: `1px solid ${T.border}`,
          borderRadius: 8, padding: '5px 11px',
          fontSize: 13, fontWeight: 600,
          fontFamily: "'Inter',sans-serif",
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          boxShadow: '0 4px 16px rgba(0,0,0,.4)',
        }}>
          {label}
        </div>
      )}
    </div>
  )
}

// ─── NavBtn ───────────────────────────────────────────────────────────────
function NavBtn({ id, icon, label, active, onNav, highlight, accentColor, open }) {
  const isActive = active === id
  const accent   = accentColor || (highlight ? T.vidyaGold : null)

  const activeBg     = accent ? `${accent}22`   : T.surfaceHi
  const activeBorder = accent ? `${accent}60`   : T.border
  const activeColor  = accent || T.textHi
  const idleColor    = T.textMid

  const btn = (
    <button
      onClick={() => onNav(id)}
      title={!open ? label : undefined}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: open ? 10 : 0,
        justifyContent: open ? 'flex-start' : 'center',
        padding: open ? '9px 12px' : '9px 0',
        marginBottom: 2,
        background:   isActive ? activeBg  : 'transparent',
        border:       isActive ? `1.5px solid ${activeBorder}` : '1.5px solid transparent',
        borderRadius: 10,
        cursor: 'pointer',
        color:  isActive ? activeColor : idleColor,
        transition: 'all .14s',
        textAlign: 'left',
        position: 'relative',
      }}
      onMouseEnter={e => {
        if (!isActive) {
          e.currentTarget.style.background   = accent ? `${accent}14` : T.surface
          e.currentTarget.style.color        = accent ? accent : T.textHi
          e.currentTarget.style.borderColor  = accent ? `${accent}35` : T.border
        }
      }}
      onMouseLeave={e => {
        if (!isActive) {
          e.currentTarget.style.background  = 'transparent'
          e.currentTarget.style.color       = idleColor
          e.currentTarget.style.borderColor = 'transparent'
        }
      }}
    >
      {isActive && (
        <div style={{
          position: 'absolute', left: 0, top: '50%',
          transform: 'translateY(-50%)',
          width: 3, height: 20, borderRadius: '0 3px 3px 0',
          background: activeColor,
        }} />
      )}

      <span style={{
        fontSize: 17,
        flexShrink: 0,
        width: 22,
        textAlign: 'center',
        filter: isActive ? 'none' : 'grayscale(20%)',
      }}>
        {icon}
      </span>

      {open && (
        <>
          <span style={{
            fontSize: 14, fontWeight: isActive ? 700 : 500,
            fontFamily: "'Inter',sans-serif",
            flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            color: 'inherit',
          }}>
            {label}
          </span>
         
        </>
      )}
    </button>
  )

  return open ? btn : <Tooltip label={label}>{btn}</Tooltip>
}

// ─── UserBadge ────────────────────────────────────────────────────────────
function UserBadge({ open }) {
  const { user, logout } = useAuth()
  if (!user) return null
  const meta     = ROLE_META[user.role] || ROLE_META.parent
  const initials = (user.name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()

  if (!open) {
    return (
      <Tooltip label={`${user.name} · ${meta.label}`}>
        <div style={{
          display: 'flex', justifyContent: 'center',
          padding: '6px 0 10px',
          borderBottom: `1px solid ${T.border}`,
          marginBottom: 8,
        }}>
          <div style={{
            width: 38, height: 38, borderRadius: '50%',
            background: meta.color,
            border: `2px solid ${meta.color}88`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 900, color: '#fff',
            fontFamily: "'Nunito',sans-serif",
            cursor: 'default',
          }}>
            {initials}
          </div>
        </div>
      </Tooltip>
    )
  }

  return (
    <div style={{
      margin: '0 10px 10px',
      background: T.surface,
      border: `1.5px solid ${T.border}`,
      borderRadius: 12,
      padding: '12px 14px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 11 }}>
        <div style={{
          width: 40, height: 40, borderRadius: '50%',
          background: meta.color,
          border: `2px solid ${meta.color}66`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, fontWeight: 900, color: '#fff', flexShrink: 0,
          fontFamily: "'Nunito',sans-serif",
        }}>
          {initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 14, fontWeight: 700, color: T.textHi,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            fontFamily: "'Nunito',sans-serif",
          }}>
            {user.name}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 3 }}>
            <span style={{ fontSize: 12 }}>{meta.icon}</span>
            <span style={{
              fontSize: 11, fontWeight: 700, letterSpacing: .3,
              color: meta.color,
              background: `${meta.color}22`,
              padding: '2px 8px', borderRadius: 20,
              fontFamily: "'Inter',sans-serif",
            }}>
              {meta.label}
            </span>
          </div>
        </div>
      </div>

      <button
        onClick={logout}
        style={{
          width: '100%', padding: '7px 0',
          background: 'transparent',
          color: T.textMid,
          border: `1px solid ${T.border}`,
          borderRadius: 8, fontSize: 12, fontWeight: 600,
          cursor: 'pointer', transition: 'all .15s',
          fontFamily: "'Inter',sans-serif",
          letterSpacing: .3,
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background  = `${T.agniCore}20`
          e.currentTarget.style.color       = T.agniGlow
          e.currentTarget.style.borderColor = `${T.agniCore}50`
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background  = 'transparent'
          e.currentTarget.style.color       = T.textMid
          e.currentTarget.style.borderColor = T.border
        }}
      >
        Sign Out
      </button>
    </div>
  )
}

// ─── StorageBar ───────────────────────────────────────────────────────────
function StorageBar({ collection, childTotal, activity, t, open }) {
  const imgCount = (collection || []).length
  const pct      = Math.min(100, (imgCount / 100) * 100)

  if (!open) return null

  return (
    <div style={{
      margin: '0 10px 10px',
      background: T.surface,
      border: `1.5px solid ${T.border}`,
      borderRadius: 10,
      padding: '11px 13px',
    }}>
      <div style={{
        fontSize: 10, fontWeight: 800, color: T.textLow,
        letterSpacing: 1.4, marginBottom: 8, textTransform: 'uppercase',
        fontFamily: "'Inter',sans-serif",
      }}>
        {t('store.label')}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
        <div style={{ flex: 1, height: 5, background: T.border, borderRadius: 3, overflow: 'hidden' }}>
          <div style={{
            width: `${pct}%`, height: '100%',
            background: `linear-gradient(to right, ${T.agniCore}, ${T.vidyaGold})`,
            borderRadius: 3, transition: 'width .4s ease',
          }} />
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, color: T.vidyaGold, fontFamily: "'Inter',sans-serif", minWidth: 42, textAlign: 'right' }}>
          {imgCount} {t('store.imgs')}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: T.blue }} />
          <span style={{ fontSize: 11, color: T.textMid, fontFamily: "'Inter',sans-serif" }}>
            {childTotal ?? '—'} {t('store.children')}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: T.green }} />
          <span style={{ fontSize: 11, color: T.textMid, fontFamily: "'Inter',sans-serif" }}>
            {(activity || []).length} {t('store.logs')}
          </span>
        </div>
      </div>
    </div>
  )
}

// ─── Lang picker ──────────────────────────────────────────────────────────
function LangPicker({ lang, onLangChange, open }) {
  if (!open) return null
  return (
    <div style={{ margin: '0 10px 10px' }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: T.textLow, letterSpacing: 1.4, marginBottom: 7, fontFamily: "'Inter',sans-serif" }}>
        LANGUAGE
      </div>
      <div style={{ display: 'flex', gap: 4 }}>
        {LANGS.map(l => (
          <button
            key={l.code}
            onClick={() => onLangChange(l.code)}
            style={{
              flex: 1, padding: '5px 0',
              border: `1.5px solid ${lang === l.code ? T.vidyaGold : T.border}`,
              borderRadius: 7,
              background: lang === l.code ? `${T.vidyaGold}22` : 'transparent',
              color: lang === l.code ? T.vidyaGold : T.textMid,
              fontSize: 11, fontWeight: 700, cursor: 'pointer',
              fontFamily: "'Inter',sans-serif",
              transition: 'all .14s',
            }}
            onMouseEnter={e => {
              if (lang !== l.code) {
                e.currentTarget.style.borderColor = `${T.vidyaGold}60`
                e.currentTarget.style.color = T.textHi
              }
            }}
            onMouseLeave={e => {
              if (lang !== l.code) {
                e.currentTarget.style.borderColor = T.border
                e.currentTarget.style.color = T.textMid
              }
            }}
          >
            {l.label}
          </button>
        ))}
      </div>
    </div>
  )
}

// ─── Sidebar ──────────────────────────────────────────────────────────────
export default function Sidebar({ active, onNav, lang, onLangChange }) {
  const t                                    = useT()
  const { user }                             = useAuth()
  const { collection, childTotal, activity } = useStore()
  const [open, setOpen]                      = useState(true)

  const role         = user?.role
  const isParent     = role === 'parent'
  const isAdmin      = role === 'admin'
  const isSuperAdmin = role === 'superadmin'
  const isStaff      = isAdmin || isSuperAdmin || role === 'teacher'
  // Added alongside the research dashboard work. Researcher is view-only by
  // construction (design brief Section 9) — it gets none of the teaching/
  // learning module links below, only the Research Dashboard entry further
  // down. Deliberately NOT folded into isStaff.
  const isResearcher  = role === 'researcher'

  const w = open ? EXPANDED_W : COLLAPSED_W

  return (
    <div style={{
      width: w,
      minWidth: w,
      background: T.nightInk,
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      position: 'sticky',
      top: 0,
      flexShrink: 0,
      overflowY: 'auto',
      overflowX: 'hidden',
      borderRight: `1px solid ${T.border}`,
      transition: 'width .25s cubic-bezier(.4,0,.2,1), min-width .25s cubic-bezier(.4,0,.2,1)',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@700;800;900&family=Inter:wght@400;500;600;700&display=swap');
        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: ${T.vidyaGold}66; }
      `}</style>

      {/* ── HEADER ── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: open ? 'space-between' : 'center',
        padding: open ? '16px 12px 14px' : '16px 0 14px',
        borderBottom: `1px solid ${T.border}`,
        marginBottom: 10,
        flexShrink: 0,
      }}>
        {open ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden' }}>
            <Mark size={38} />
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontFamily: "'Nunito',sans-serif", fontSize: 18, fontWeight: 900,
                color: T.textHi, lineHeight: 1, letterSpacing: -.3, whiteSpace: 'nowrap',
              }}>
                Vidyagni
              </div>
              <div style={{
                fontSize: 10, color: T.textLow,
                marginTop: 3, fontFamily: "'Inter',sans-serif",
                letterSpacing: .5, whiteSpace: 'nowrap',
              }}>
                {t('platform.name')}
              </div>
            </div>
          </div>
        ) : (
          <Mark size={34} />
        )}

        <button
          onClick={() => setOpen(o => !o)}
          title={open ? 'Collapse sidebar' : 'Expand sidebar'}
          style={{
            background: 'transparent',
            border: `1px solid ${T.border}`,
            borderRadius: 8,
            width: 34, height: 34,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all .15s',
            flexShrink: 0,
            padding: 0,
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background   = T.surfaceHi
            e.currentTarget.style.borderColor  = T.vidyaGold + '80'
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background  = 'transparent'
            e.currentTarget.style.borderColor = T.border
          }}
        >
          <HamburgerIcon open={open} />
        </button>
      </div>

      {/* ── USER BADGE ── */}
      <UserBadge open={open} />

      {/* ── LANGUAGE PICKER ── */}
      <LangPicker lang={lang} onLangChange={onLangChange} open={open} />

      {/* ── STORAGE BAR ── */}
      {isStaff && (
        <StorageBar
          collection={collection}
          childTotal={childTotal}
          activity={activity}
          t={t}
          open={open}
        />
      )}

      <Divider />

      {/* ── NAV ── */}
      <div style={{ padding: open ? '0 8px' : '0 6px', flex: 1 }}>

        <SectionLabel open={open}>{t('nav.overview')}</SectionLabel>
        <NavBtn
          id="dashboard" icon="🏠"
          label={t('nav.dashboard')}
          active={active} onNav={onNav} open={open}
        />

        {/* Learning Modules + content Modules are hidden entirely for
            researcher accounts — those routes aren't part of the research
            role's scope (view-only, GET-only /api/research/* per design
            brief Section 9), so showing them would just be a sidebar full
            of links that 403 when clicked. */}
        {!isResearcher && (
          <>
            <Divider />
            <SectionLabel open={open}>Learning Modules</SectionLabel>
            {LEARNING_MODS.map(m => (
              <NavBtn
                key={m.id} id={m.id} icon={m.icon}
                label={m.label}
                active={active} onNav={onNav}
                highlight={m.highlight}
                open={open}
              />
            ))}

            <Divider />
            <SectionLabel open={open}>{t('nav.modules')}</SectionLabel>
            {CONTENT_MODS
              // CHANGE 2: srt-screener hidden from parents
              .filter(m => !isParent || !['collection', 'readiness', 'flashcard', 'worksheet', 'activity', 'storybook', 'srt-screener'].includes(m.id))
              .map(m => (
                <NavBtn
                  key={m.id} id={m.id} icon={m.icon}
                  // CHANGE 3: use m.label if present (for srt-screener), else i18n key
                  label={m.label || t(`mod.${m.id}`)}
                  active={active} onNav={onNav}
                  accentColor={m.color}
                  open={open}
                />
              ))}
          </>
        )}

        {/* PLATFORM section — only rendered at all if something inside it
            would actually show for this role, so a pure researcher account
            never sees a floating "PLATFORM" label with nothing under it. */}
        {(isStaff || isAdmin || isSuperAdmin || isResearcher) && (
          <>
            <Divider />
            <SectionLabel open={open}>{t('nav.platform')}</SectionLabel>

            {isStaff && (
              <NavBtn
                id="children" icon="👤"
                label={t('nav.children')}
                active={active} onNav={onNav}
                accentColor={T.teal}
                open={open}
              />
            )}

            {(isAdmin || isSuperAdmin) && (
              <NavBtn
                id="admin" icon="⚙️"
                label="Admin Panel"
                active={active} onNav={onNav}
                accentColor={T.agniGlow}
                open={open}
              />
            )}

            {/* Research Dashboard — both superadmin (no new role needed)
                and the new 'researcher' role get this, per design brief
                Section 1/9. Now the across-group squares grid only —
                Per-Child View is its own page/nav entry below. */}
            {(isSuperAdmin || isResearcher) && (
              <>
                <NavBtn
                  id="research" icon="🔬"
                  label="Research Dashboard"
                  active={active} onNav={onNav}
                  accentColor={T.teal}
                  open={open}
                />
                <NavBtn
                  id="research/children" icon="🧒"
                  label="Per-Child View"
                  active={active} onNav={onNav}
                  accentColor={T.teal}
                  open={open}
                />
                <NavBtn
                  id="research/calibration" icon="📐"
                  label="Calibration"
                  active={active} onNav={onNav}
                  accentColor={T.teal}
                  open={open}
                />
              </>
            )}

            {/* Clinical Rating — write-side, staff only (the same set that
                can conduct QuickScreener/SRT in the first place). Never
                shown to 'researcher', which stays view-only by design. */}
            {isStaff && (
              <NavBtn
                id="clinical-ratings/new" icon="🩺"
                label="Clinical Rating"
                active={active} onNav={onNav}
                accentColor={T.amber}
                open={open}
              />
            )}

            {(isAdmin || isSuperAdmin) && (
              <NavBtn
                id="settings" icon="🔧"
                label={t('nav.settings')}
                active={active} onNav={onNav}
                open={open}
              />
            )}
          </>
        )}

        {isSuperAdmin && (
          <>
            <Divider />
            <SectionLabel open={open}>Public Reports</SectionLabel>
            <NavBtn
              id="public-srt" icon="🌐"
              label="Public SRT Reports"
              active={active} onNav={onNav}
              accentColor="#16A34A"
              open={open}
            />
          </>
        )}

      </div>

      {/* ── FOOTER ── */}
      <div style={{
        padding: open ? '12px 14px' : '12px 0',
        borderTop: `1px solid ${T.border}`,
        marginTop: 8,
        flexShrink: 0,
        display: 'flex',
        flexDirection: open ? 'column' : 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: open ? 5 : 0,
      }}>
        {open ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Mark size={16} />
              <span style={{
                fontSize: 11, fontWeight: 700, color: T.textLow,
                fontFamily: "'Inter',sans-serif", letterSpacing: .4,
              }}>
                Vidyagni · NISH Trivandrum
              </span>
            </div>
            <div style={{
              fontSize: 10, color: T.textLow,
              fontFamily: "'Inter',sans-serif", lineHeight: 1.6, letterSpacing: .3,
              opacity: .7,
            }}>
              NCF-FS 2022 · NEP 2020 · v1.0
            </div>
          </>
        ) : (
          <Mark size={20} />
        )}
      </div>
    </div>
  )
}